import { useEffect, useState, useCallback } from "react";
import { Product, ProductItem } from "../api-client";
import { fetchProductDetail } from "../api/productDetail";

interface UseProductDetailReturn {
  productDetail: Product | null;
  productItems: ProductItem[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  updateProductItemStock: (colorId: number, sizeId: number, newStock: number) => void;
}

export const useProductDetail = (id: number, enabled = true): UseProductDetailReturn => {
  const [productDetail, setProductDetail] = useState<Product | null>(null);
  const [productItems, setProductItems] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled || !id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchProductDetail(id);
      setProductDetail(data);

      if (data.product_items && Array.isArray(data.product_items)) {
        const organizedItems = data.product_items.map(item => ({
          ...item,
          color: item.distribution?.color,
          size: item.distribution?.size,
          stock: item.distribution?.stock,
          images: item.images || [],
        }));
        setProductItems(organizedItems);
      } else {
        setProductItems([]);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [enabled, id]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const updateProductItemStock = useCallback((colorId: number, sizeId: number, newStock: number) => {
    setProductItems(prevItems => 
      prevItems.map(item => {
        if (
          item.distribution?.color?.id === colorId && 
          item.distribution?.size?.id === sizeId
        ) {
          return {
            ...item,
            distribution: {
              ...item.distribution,
              stock: newStock
            }
          };
        }
        return item;
      })
    );

    // También actualizar el stock en productDetail si es necesario
    setProductDetail(prevDetail => {
      if (!prevDetail) return null;
      
      return {
        ...prevDetail,
        product_items: (prevDetail.product_items || []).map(item => {
          if (
            item.distribution?.color?.id === colorId && 
            item.distribution?.size?.id === sizeId
          ) {
            return {
              ...item,
              distribution: {
                ...item.distribution,
                stock: newStock
              }
            };
          }
          return item;
        })
      };
    });
  }, []);

  useEffect(() => {
    if (enabled && id) {
      fetchData();
    }
  }, [enabled, fetchData, id]);

  return { 
    productDetail, 
    productItems, 
    loading, 
    error, 
    refresh,
    updateProductItemStock 
  };
};